import React, { useState, useEffect } from "react";
import config from '../config/config';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button,
  CircularProgress, Grid, TextField, Typography, IconButton,
  Select, MenuItem, FormControl, InputLabel, Box, Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Close, Edit, Add } from "@mui/icons-material";
import axios from "axios";

const T = {
  white: '#ffffff',
  bg: '#f8f9fc',
  border: '#e8ecf4',
  borderLight: '#f0f3fb',
  accent: '#5b4fcf',
  accentLight: '#ede9fd',
  accentMid: '#7c6fe0',
  text: '#1a1d2e',
  textSub: '#6b7280',
  textMuted: '#9ca3af',
  error: '#dc2626',
  errorBg: '#fef2f2',
  shadow: '0 4px 24px rgba(0,0,0,0.08)',
  radius: '18px',
};

const pillBtn = (variant = 'accent') => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontFamily: 'Quicksand, sans-serif',
  fontSize: '13px',
  px: 2.5,
  py: 1,
  transition: 'all 0.18s ease',
  ...(variant === 'accent' && {
    background: T.accent,
    color: '#fff',
    border: 'none',
    '&:hover': { background: T.accentMid, boxShadow: '0 4px 14px rgba(91,79,207,0.35)' },
    '&.Mui-disabled': { background: '#c4c4c4', color: '#fff' },
  }),
  ...(variant === 'outline' && {
    background: T.white,
    color: T.accent,
    border: `1.5px solid ${T.accent}`,
    '&:hover': { background: T.accentLight },
  }),
  ...(variant === 'ghost' && {
    background: T.bg,
    color: T.textSub,
    border: `1px solid ${T.border}`,
    '&:hover': { background: T.border },
  }),
});

const styledInput = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: 'Quicksand, sans-serif',
    fontWeight: 600,
    fontSize: '13px',
    background: T.white,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.accent },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.accent, borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: '13px' },
  '& .MuiInputLabel-root.Mui-focused': { color: T.accent },
};

const InvoicePaymentDialog = ({ open, onClose, onSubmit, invoice, invoiceItems = [] }) => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchItems = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${config.API_URL}/api/pullproductitems/Fee`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        const sortedItems = (res.data.invoiceItems || []).sort((a, b) => a.name.localeCompare(b.name));
        setItems(sortedItems);
      } catch (err) {
        setMessage('Failed to load items from the server.');
      }
    };
    if (open) fetchItems();
  }, [open]);

  useEffect(() => {
    if (open) {
      const initialRows = invoiceItems.map((item) => {
        const matchedItem = items.find((apiItem) => apiItem.name === item.description) || {};
        return {
          id: item.id,
          description: item.description || "",
          quantity: item.quantity || "1",
          total: item.total || matchedItem.price || "0.00",
          amountPaid: (parseFloat(item.total || matchedItem.price || 0) - parseFloat(item.paid || 0)).toFixed(2),
          isNew: false,
        };
      });

      const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      };

      setRows((prevRows) => {
        if (prevRows.length === 0 || prevRows.every((row) => !row.isNew)) return initialRows;
        return prevRows;
      });
      setInvoiceDate(formatDate(invoice.invoiceIssueDate));
      setDueDate(formatDate(invoice.invoiceDueDate));
      setPaymentNote(invoice.note || "");
      setErrors({});
      setMessage(null);
      setLoading(false);
    }
  }, [open, invoiceItems, invoice, items]);

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    if (field === "description") {
      const selectedItem = items.find((item) => item.name === value);
      updatedRows[index].total = selectedItem
        ? (parseFloat(selectedItem.price || 0) * parseFloat(updatedRows[index].quantity || 1)).toFixed(2)
        : "0.00";
    }
    if (field === "quantity") {
      const selectedItem = items.find((item) => item.name === updatedRows[index].description);
      if (selectedItem) updatedRows[index].total = (parseFloat(selectedItem.price || 0) * parseFloat(value || 1)).toFixed(2);
    }
    setRows(updatedRows);
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      { id: `new-${Date.now()}`, description: "", quantity: "1", total: "0.00", amountPaid: "0.00", isNew: true },
    ]);
  };

  const handleDeleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const handleEditSave = async () => {
    setLoading(true);
    setErrors({});
    setMessage(null);
    const newErrors = {};
    rows.forEach((row, index) => {
      if (!row.description) newErrors[`description-${index}`] = "Description is required";
      if (!row.total || row.total < 0) newErrors[`total-${index}`] = "Total must be non-negative";
    });
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); setLoading(false); return; }
    try {
      const payload = {
        invoice_id: invoice.id,
        invoice_date: invoiceDate,
        due_date: dueDate,
        payment_note: paymentNote,
        payments: rows.map((row) => ({
          ...(row.isNew ? {} : { item_id: row.id }),
          description: row.description,
          quantity: parseFloat(row.quantity),
          total: parseFloat(row.total),
          amount_paid: parseFloat(row.amountPaid),
        })),
      };
      const response = await onSubmit(payload);
      if (response?.errors) setErrors(response.errors);
      else if (response?.message) setMessage(response.message);
      if (!response?.errors) onClose();
    } catch (error) {
      setMessage("An error occurred during saving.");
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = rows.reduce((s, r) => s + (parseFloat(r.total) || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{
      sx: {
        borderRadius: T.radius,
        border: `1px solid ${T.border}`,
        boxShadow: T.shadow,
        fontFamily: 'Quicksand, sans-serif',
        background: T.white,
      }
    }}>
      <DialogTitle sx={{ pb: 2, borderBottom: `1px solid ${T.border}`, px: 3.5, pt: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: T.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Edit sx={{ fontSize: 20, color: T.accent }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: T.text, fontFamily: 'Quicksand, sans-serif' }}>
                Edit Invoice
              </Typography>
              <Typography sx={{ fontSize: '12px', color: T.textSub, fontFamily: 'Quicksand, sans-serif' }}>
                #{invoice?.invoiceNumber}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3.5, py: 3 }}>
        {message && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px', fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
            {message}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField
              label="Invoice Date"
              type="date"
              fullWidth
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              error={!!errors.invoiceDate}
              helperText={errors.invoiceDate}
              sx={styledInput}
            />
          </Grid>
          <Grid item xs={6} />
        </Grid>

        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: T.text, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Line Items
        </Typography>

        {rows.map((row, index) => (
          <Box key={row.id} sx={{
            background: T.bg, borderRadius: '12px', border: `1px solid ${T.borderLight}`,
            px: 2.5, py: 2, mb: 1.5, position: 'relative',
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={row.isNew ? 5 : 6}>
                <FormControl fullWidth error={!!errors[`description-${index}`]} sx={styledInput}>
                  <InputLabel id={`description-label-${index}`}>Description</InputLabel>
                  <Select
                    labelId={`description-label-${index}`}
                    value={items.find((item) => item.name === row.description) ? row.description : ""}
                    onChange={(e) => handleInputChange(index, "description", e.target.value)}
                    disabled={loading || !row.isNew}
                    label="Description"
                  >
                    <MenuItem value="" sx={{ fontFamily: 'Quicksand, sans-serif', fontSize: '13px' }}>Select an item</MenuItem>
                    {items.map((item) => (
                      <MenuItem key={item.id} value={item.name} sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: '13px' }}>
                        {item.name} — {parseFloat(item.price).toFixed(2)}
                      </MenuItem>
                    ))}
                    {!row.isNew && row.description && !items.find((item) => item.name === row.description) && (
                      <MenuItem value={row.description} disabled sx={{ fontFamily: 'Quicksand, sans-serif', fontSize: '13px' }}>
                        {row.description} (Not in current list)
                      </MenuItem>
                    )}
                  </Select>
                  {!!errors[`description-${index}`] && (
                    <Typography color="error" variant="caption" sx={{ fontFamily: 'Quicksand, sans-serif', mt: 0.5, ml: 1.5 }}>
                      {errors[`description-${index}`]}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={row.isNew ? 4 : 6}>
                <TextField
                  label="Total (KES)"
                  type="number"
                  fullWidth
                  value={row.total}
                  onChange={(e) => handleInputChange(index, "total", e.target.value)}
                  disabled={loading || row.isNew}
                  error={!!errors[`total-${index}`]}
                  helperText={errors[`total-${index}`]}
                  sx={styledInput}
                />
              </Grid>
              {row.isNew && (
                <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconButton
                    onClick={() => handleDeleteRow(index)}
                    disabled={loading}
                    sx={{ color: T.error, background: T.errorBg, borderRadius: '10px', '&:hover': { background: '#fecaca' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}

        <Button
          variant="outlined"
          onClick={handleAddRow}
          disabled={loading}
          startIcon={<Add sx={{ fontSize: 16 }} />}
          sx={{ ...pillBtn('outline'), mt: 0.5, mb: 2.5 }}
        >
          Add Line Item
        </Button>

        {rows.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
            <Box sx={{ background: T.accentLight, borderRadius: '12px', px: 2.5, py: 1.5, border: `1px dashed #c4bcf7` }}>
              <Typography sx={{ fontSize: '12px', color: T.accent, fontWeight: 600, fontFamily: 'Quicksand, sans-serif' }}>
                Invoice Total
              </Typography>
              <Typography sx={{ fontSize: '22px', fontWeight: 700, color: T.accent, fontFamily: 'Quicksand, sans-serif', lineHeight: 1.2 }}>
                {new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(grandTotal)}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ height: '1px', background: T.border, mb: 2.5 }} />

        <TextField
          label="Invoice Note (optional)"
          fullWidth
          multiline
          rows={3}
          value={paymentNote}
          onChange={(e) => setPaymentNote(e.target.value)}
          disabled={loading}
          sx={styledInput}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3.5, pb: 3.5, pt: 1, gap: 1.5 }}>
        <Button onClick={onClose} disabled={loading} sx={pillBtn('ghost')}>Cancel</Button>
        <Button
          onClick={handleEditSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Edit sx={{ fontSize: 16 }} />}
          sx={pillBtn('accent')}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePaymentDialog;